import { Input, Textarea } from '@nextui-org/react'
import axios from 'axios'
import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useLocation } from 'react-router-dom'

import {
    SUPPORT_ATTACHMENT_RULES,
    SupportAttachment,
    createSupportAttachment,
    formatSupportAttachmentSize,
    getSupportAttachmentError
} from './support/supportAttachments'

import { Icons } from '@/components/icons'
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader
} from '@/components/nextui'
import { env } from '@/configs/env'
import { useToast } from '@/context/ToastContext'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'

interface SupportContextType {
    openSupport: () => void
    submitSupportRequest: () => Promise<boolean>
}

interface SupportFormState {
    name: string
    email: string
    cellphone: string
    documentNumber: string
    issueType: string
    message: string
    attachments: SupportAttachment[]
}

interface SupportAttachmentPayload {
    name: string
    type: string
    size: number
    previewDataUrl: string | null
}

interface SupportRequestPayload {
    name: string
    email: string
    cellphone: string
    documentNumber: string
    issueType: string
    message: string
    attachments: SupportAttachmentPayload[]
    sourcePath: string
    sourceEntryPoint: string
    aspirantId: number | null
    isAuthenticated: boolean
    createdAt: string
    userAgent: string
}

interface SupportController {
    form: SupportFormState
    isOpen: boolean
    isSubmitting: boolean
    isVisible: boolean
    hasEndpointConfigured: boolean
    closeSupport: () => void
    openSupport: () => void
    submitSupportRequest: () => Promise<boolean>
    addAttachments: (files: FileList | null) => Promise<void>
    removeAttachment: (attachmentId: string) => void
    updateField: (
        field: keyof Omit<SupportFormState, 'attachments'>,
        value: string
    ) => void
}

interface SupportProfileDefaults {
    firstName: string | null
    lastName: string | null
    email: string | null
    cellphone: string | null
    documentNumber: string | null
}

const SUPPORT_ISSUES = [
    { value: 'registro', label: 'Registro o creacion de cuenta' },
    { value: 'login', label: 'Acceso o recuperacion de clave' },
    { value: 'inscripcion', label: 'Proceso de inscripcion' },
    { value: 'documentos', label: 'Carga de documentos' },
    { value: 'pagos', label: 'Pagos u orden de matricula' },
    { value: 'general', label: 'Otra consulta' }
] as const

const SUPPORT_ENTRY_POINT_STORAGE_KEY = 'support_source_entry_point'
const SUPPORT_SOURCE_ENTRY_POINT = 'Journey Web'
const SUPPORT_MAX_TEXT_LENGTH = 255
const SUPPORT_MAX_MESSAGE_LENGTH = 2000

const SupportContext = createContext<SupportContextType | undefined>(undefined)

const buildFullName = (
    firstName: string | null,
    lastName: string | null
): string => {
    return [firstName, lastName].filter(Boolean).join(' ').trim()
}

const normalizeDocumentNumber = (value: unknown): string => {
    if (value === null || value === undefined) {
        return ''
    }

    return String(value).trim()
}

const truncateSupportText = (
    value: string,
    maxLength = SUPPORT_MAX_TEXT_LENGTH
): string => {
    const trimmedValue = value.trim()

    if (trimmedValue.length <= maxLength) {
        return trimmedValue
    }

    return `${trimmedValue.slice(0, Math.max(0, maxLength - 3))}...`
}

const buildSupportEntryPoint = (): string => {
    return SUPPORT_SOURCE_ENTRY_POINT
}

const buildSupportSourcePath = (pathname: string, hash: string): string => {
    return truncateSupportText(`${pathname}${hash}`)
}

const buildDefaultFormState = ({
    firstName,
    lastName,
    email,
    cellphone,
    documentNumber
}: SupportProfileDefaults): SupportFormState => ({
    name: buildFullName(firstName, lastName),
    email: email || '',
    cellphone: cellphone || '',
    documentNumber: documentNumber || '',
    issueType: 'general',
    message: '',
    attachments: []
})

const useSupportFormState = (defaults: SupportProfileDefaults) => {
    const [form, setForm] = useState<SupportFormState>(() =>
        buildDefaultFormState(defaults)
    )

    const resetForm = useCallback(() => {
        setForm(buildDefaultFormState(defaults))
    }, [defaults])

    useEffect(() => {
        setForm((currentForm) => ({
            ...currentForm,
            name:
                currentForm.name ||
                buildFullName(defaults.firstName, defaults.lastName),
            email: currentForm.email || defaults.email || '',
            cellphone: currentForm.cellphone || defaults.cellphone || '',
            documentNumber:
                currentForm.documentNumber || defaults.documentNumber || ''
        }))
    }, [defaults])

    const updateField = useCallback(
        (field: keyof Omit<SupportFormState, 'attachments'>, value: string) => {
            setForm((currentForm) => ({
                ...currentForm,
                [field]: value
            }))
        },
        []
    )

    const appendAttachments = useCallback(
        (attachments: SupportAttachment[]) => {
            setForm((currentForm) => ({
                ...currentForm,
                attachments: [...currentForm.attachments, ...attachments].slice(
                    0,
                    SUPPORT_ATTACHMENT_RULES.maxFiles
                )
            }))
        },
        []
    )

    const removeAttachment = useCallback((attachmentId: string) => {
        setForm((currentForm) => ({
            ...currentForm,
            attachments: currentForm.attachments.filter(
                (attachment) => attachment.id !== attachmentId
            )
        }))
    }, [])

    return {
        appendAttachments,
        form,
        removeAttachment,
        resetForm,
        updateField
    }
}

export const useSupport = (): SupportContextType => {
    const context = useContext(SupportContext)

    if (!context) {
        throw new Error('useSupport must be used within a SupportProvider')
    }

    return context
}

export const SupportProvider: React.FC<{ children: ReactNode }> = ({
    children
}) => {
    const support = useSupportController()
    const contextValue = useMemo(
        () => ({
            openSupport: support.openSupport,
            submitSupportRequest: support.submitSupportRequest
        }),
        [support.openSupport, support.submitSupportRequest]
    )

    return (
        <SupportContext.Provider value={contextValue}>
            {children}
            {support.isVisible && (
                <SupportFloatingButton onClick={support.openSupport} />
            )}
        </SupportContext.Provider>
    )
}

// eslint-disable-next-line max-lines-per-function
const useSupportController = (): SupportController => {
    const location = useLocation()
    const { showToast } = useToast()
    const {
        firstName,
        lastName,
        email,
        cellphone,
        idAspirant,
        isAuthenticated,
        isAgent
    } = useAuthStore()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.NONE,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [profileDocumentNumber, setProfileDocumentNumber] =
        useState<string>('')
    const getBasicDataRef = useRef(aspirantsApi.getBasicData)
    const loadedDocumentNumberAspirantRef = useRef<number | null>(null)

    useEffect(() => {
        getBasicDataRef.current = aspirantsApi.getBasicData
    }, [aspirantsApi])

    useEffect(() => {
        if (!isAuthenticated || !idAspirant || isAgent) {
            loadedDocumentNumberAspirantRef.current = null
            setProfileDocumentNumber('')

            return
        }

        if (loadedDocumentNumberAspirantRef.current === idAspirant) {
            return
        }

        setProfileDocumentNumber('')

        let isCancelled = false

        const loadDocumentNumber = async () => {
            try {
                const { data, status } =
                    await getBasicDataRef.current(idAspirant)

                if (isCancelled) {
                    return
                }

                if (status !== 200) {
                    loadedDocumentNumberAspirantRef.current = null

                    return
                }

                loadedDocumentNumberAspirantRef.current = idAspirant
                setProfileDocumentNumber(
                    normalizeDocumentNumber(data?.documentNumber)
                )
            } catch (error) {
                loadedDocumentNumberAspirantRef.current = null

                if (process.env.NODE_ENV === 'development') {
                    console.error(
                        '[SupportProvider] Error loading document number',
                        error
                    )
                }
            }
        }

        void loadDocumentNumber()

        return () => {
            isCancelled = true
        }
    }, [idAspirant, isAuthenticated, isAgent])

    const profileDefaults = useMemo(
        () => ({
            firstName,
            lastName,
            email,
            cellphone,
            documentNumber: profileDocumentNumber
        }),
        [cellphone, email, firstName, lastName, profileDocumentNumber]
    )
    const {
        appendAttachments,
        form,
        removeAttachment,
        resetForm,
        updateField
    } = useSupportFormState(profileDefaults)

    const currentPath = useMemo(
        () => buildSupportSourcePath(location.pathname, location.hash),
        [location.hash, location.pathname]
    )
    const sourceEntryPoint = useMemo(() => buildSupportEntryPoint(), [])

    useEffect(() => {
        try {
            sessionStorage.setItem(
                SUPPORT_ENTRY_POINT_STORAGE_KEY,
                buildSupportEntryPoint()
            )
        } catch {}
    }, [])

    const openSupport = useCallback(() => {
        window.open('https://universidadean.edu.co/meli-turno', '_blank', 'noopener,noreferrer')
    }, [])

    const closeSupport = useCallback(() => {
        setIsOpen(false)
    }, [])

    const addAttachments = useCallback(
        async (files: FileList | null): Promise<void> => {
            if (!files?.length) {
                return
            }

            const selectedFiles = Array.from(files)
            const remainingSlots =
                SUPPORT_ATTACHMENT_RULES.maxFiles - form.attachments.length

            if (remainingSlots <= 0) {
                showToast({
                    message: `Solo puedes adjuntar hasta ${SUPPORT_ATTACHMENT_RULES.maxFiles} imagenes.`,
                    type: 'warning'
                })

                return
            }

            const filesToProcess = selectedFiles.slice(0, remainingSlots)
            const invalidMessage = filesToProcess
                .map((file) => getSupportAttachmentError(file))
                .find(Boolean)

            if (invalidMessage) {
                showToast({
                    message: invalidMessage,
                    type: 'warning'
                })

                return
            }

            if (selectedFiles.length > remainingSlots) {
                showToast({
                    message: `Solo se agregaran ${remainingSlots} archivo(s) para respetar el maximo permitido.`,
                    type: 'warning'
                })
            }

            try {
                const nextAttachments = await Promise.all(
                    filesToProcess.map(createSupportAttachment)
                )

                appendAttachments(nextAttachments)
            } catch (error) {
                console.error('Error preparing support attachments', error)
                showToast({
                    message:
                        'No pudimos preparar las imagenes adjuntas. Intenta con otra captura.',
                    type: 'error'
                })
            }
        },
        [appendAttachments, form.attachments.length, showToast]
    )

    const submitSupportRequest = useCallback(async (): Promise<boolean> => {
        const trimmedForm = {
            name: truncateSupportText(form.name),
            email: truncateSupportText(form.email),
            cellphone: truncateSupportText(form.cellphone),
            documentNumber: truncateSupportText(
                form.documentNumber || profileDocumentNumber
            ),
            issueType: truncateSupportText(form.issueType),
            message: truncateSupportText(
                form.message,
                SUPPORT_MAX_MESSAGE_LENGTH
            ),
            attachments: form.attachments
        }

        if (
            !trimmedForm.name ||
            !trimmedForm.email ||
            !trimmedForm.documentNumber ||
            !trimmedForm.issueType ||
            !trimmedForm.message
        ) {
            showToast({
                message:
                    'Completa nombre, correo, numero de documento, tipo de ayuda y mensaje antes de enviar.',
                type: 'warning'
            })

            return false
        }

        if (!env.supportFormUrl) {
            showToast({
                message:
                    'Falta configurar VITE_SUPPORT_FORM_URL para enviar el formulario al flujo de Teams.',
                type: 'warning'
            })

            return false
        }

        const payload: SupportRequestPayload = {
            ...trimmedForm,
            attachments: trimmedForm.attachments.map((attachment) => ({
                name: attachment.name,
                type: attachment.type,
                size: attachment.size,
                previewDataUrl: attachment.previewDataUrl
            })),
            sourcePath: currentPath,
            sourceEntryPoint,
            aspirantId: idAspirant,
            isAuthenticated,
            createdAt: new Date().toISOString(),
            userAgent: window.navigator.userAgent
        }

        setIsSubmitting(true)

        try {
            await axios.post(env.supportFormUrl, payload, {
                headers: {
                    'Content-Type': 'application/json'
                },
                timeout: 15000
            })

            showToast({
                message:
                    'Tu solicitud fue enviada. El equipo de soporte la revisara en breve.',
                type: 'success'
            })
            closeSupport()
            resetForm()

            return true
        } catch (error) {
            console.error('Error submitting support request', error)
            showToast({
                message:
                    'No pudimos enviar la solicitud en este momento. Intenta nuevamente en unos minutos.',
                type: 'error'
            })

            return false
        } finally {
            setIsSubmitting(false)
        }
    }, [
        closeSupport,
        currentPath,
        form,
        idAspirant,
        isAuthenticated,
        profileDocumentNumber,
        resetForm,
        sourceEntryPoint,
        showToast
    ])

    return {
        form,
        isOpen,
        isSubmitting,
        isVisible: !(isAgent || location.pathname === '/agent-impersonate'),
        hasEndpointConfigured: Boolean(env.supportFormUrl),
        addAttachments,
        closeSupport,
        openSupport,
        removeAttachment,
        submitSupportRequest,
        updateField
    }
}

const SupportFloatingButton = ({
    onClick
}: {
    onClick: () => void
}): JSX.Element => {
    return (
        <button
            type="button"
            onClick={onClick}
            className="support-fab fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-full bg-[#005BB5] px-4 py-3 text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-[#004991] focus:outline-none focus:ring-2 focus:ring-[#00BF68] focus:ring-offset-2 md:bottom-6 md:right-6"
            aria-label="Abrir opciones de ayuda"
            title="Necesitas ayuda"
        >
            <Icons.Support className="h-5 w-5" />
            <span className="hidden text-sm font-semibold md:inline">
                Ayuda
            </span>
        </button>
    )
}

const SupportModal = ({
    isOpen,
    isSubmitting,
    form,
    hasEndpointConfigured,
    onClose,
    onSubmit,
    onAddAttachments,
    onChange,
    onRemoveAttachment
}: {
    isOpen: boolean
    isSubmitting: boolean
    form: SupportFormState
    hasEndpointConfigured: boolean
    onClose: () => void
    onSubmit: () => Promise<boolean>
    onAddAttachments: (files: FileList | null) => Promise<void>
    onChange: (
        field: keyof Omit<SupportFormState, 'attachments'>,
        value: string
    ) => void
    onRemoveAttachment: (attachmentId: string) => void
}): JSX.Element => {
    return (
        <Modal
            backdrop="opaque"
            isOpen={isOpen}
            onClose={onClose}
            placement="center"
            scrollBehavior="inside"
        >
            <ModalContent>
                {() => (
                    <>
                        <ModalHeader className="flex flex-col gap-1 pb-2">
                            <div className="flex items-center gap-2 text-primary">
                                <Icons.Support className="h-5 w-5" />
                                <span className="text-lg font-semibold">
                                    Centro de ayuda
                                </span>
                            </div>
                            <p className="text-sm text-default-500">
                                Completa el formulario y envianos tu caso.
                            </p>
                        </ModalHeader>
                        <ModalBody className="gap-3 pt-0">
                            <SupportEndpointHint
                                hasEndpointConfigured={hasEndpointConfigured}
                            />
                            <SupportFormFields
                                form={form}
                                onAddAttachments={onAddAttachments}
                                onChange={onChange}
                                onRemoveAttachment={onRemoveAttachment}
                            />
                        </ModalBody>
                        <ModalFooter className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
                            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                                <Button
                                    className="w-full sm:w-auto"
                                    variant="light"
                                    onPress={onClose}
                                >
                                    Cerrar
                                </Button>
                                <Button
                                    className="w-full sm:w-auto"
                                    color="primary"
                                    isLoading={isSubmitting}
                                    onPress={onSubmit}
                                >
                                    Enviar caso
                                </Button>
                            </div>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    )
}

const SupportEndpointHint = ({
    hasEndpointConfigured
}: {
    hasEndpointConfigured: boolean
}): JSX.Element | null => {
    if (hasEndpointConfigured) {
        return null
    }

    return (
        <div className="rounded-xl border border-warning/30 bg-warning/10 p-3 text-sm text-warning-700">
            Configura <code>VITE_SUPPORT_FORM_URL</code> para enviar este
            formulario a tu backend, Power Automate o flujo que publique en
            Teams.
        </div>
    )
}

const SupportFormFields = ({
    form,
    onAddAttachments,
    onChange,
    onRemoveAttachment
}: {
    form: SupportFormState
    onAddAttachments: (files: FileList | null) => Promise<void>
    onChange: (
        field: keyof Omit<SupportFormState, 'attachments'>,
        value: string
    ) => void
    onRemoveAttachment: (attachmentId: string) => void
}): JSX.Element => {
    return (
        <>
            <div className="grid gap-3 md:grid-cols-2">
                <Input
                    label="Nombre"
                    variant="bordered"
                    value={form.name}
                    onValueChange={(value) => onChange('name', value)}
                />
                <Input
                    label="Correo"
                    type="email"
                    variant="bordered"
                    value={form.email}
                    onValueChange={(value) => onChange('email', value)}
                />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
                <Input
                    label="Celular"
                    variant="bordered"
                    value={form.cellphone}
                    onValueChange={(value) => onChange('cellphone', value)}
                />
                <Input
                    label="Numero de documento"
                    isRequired
                    variant="bordered"
                    value={form.documentNumber}
                    onValueChange={(value) => onChange('documentNumber', value)}
                />
            </div>

            <label className="flex flex-col gap-1 text-sm text-default-700">
                <span className="font-medium">Tipo de ayuda</span>
                <select
                    className="min-h-14 rounded-xl border border-default-200 bg-transparent px-3 py-2 text-sm outline-none transition-colors focus:border-primary"
                    value={form.issueType}
                    onChange={(event) =>
                        onChange('issueType', event.target.value)
                    }
                >
                    {SUPPORT_ISSUES.map((issue) => (
                        <option
                            key={issue.value}
                            value={issue.value}
                        >
                            {issue.label}
                        </option>
                    ))}
                </select>
            </label>

            <Textarea
                label="Cuentanos que paso"
                placeholder="Describe el problema, el paso donde ocurrio y que estabas intentando hacer."
                minRows={5}
                variant="bordered"
                value={form.message}
                onValueChange={(value) => onChange('message', value)}
            />

            <SupportAttachmentField
                attachments={form.attachments}
                onAddAttachments={onAddAttachments}
                onRemoveAttachment={onRemoveAttachment}
            />
        </>
    )
}

const SupportAttachmentField = ({
    attachments,
    onAddAttachments,
    onRemoveAttachment
}: {
    attachments: SupportAttachment[]
    onAddAttachments: (files: FileList | null) => Promise<void>
    onRemoveAttachment: (attachmentId: string) => void
}): JSX.Element => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    return (
        <div className="rounded-xl border border-default-200 bg-default-50/60 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                    <p className="text-sm font-semibold text-default-800">
                        Capturas opcionales
                    </p>
                    <p className="text-xs text-default-500">
                        Adjunta hasta {SUPPORT_ATTACHMENT_RULES.maxFiles}{' '}
                        imagenes PNG, JPG o WEBP de maximo{' '}
                        {SUPPORT_ATTACHMENT_RULES.maxSizeMb} MB cada una.
                    </p>
                    <p className="text-xs text-default-400">
                        Si la imagen es pesada, enviaremos la referencia del
                        archivo aunque la vista previa no quepa en Teams.
                    </p>
                </div>
                <div className="shrink-0">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={SUPPORT_ATTACHMENT_RULES.accept}
                        className="hidden"
                        multiple
                        onChange={(event) => {
                            void onAddAttachments(event.target.files)
                            event.target.value = ''
                        }}
                    />
                    <Button
                        color="primary"
                        variant="flat"
                        startContent={
                            <Icons.Upload className="h-4 w-4 shrink-0" />
                        }
                        isDisabled={
                            attachments.length >=
                            SUPPORT_ATTACHMENT_RULES.maxFiles
                        }
                        onPress={() => fileInputRef.current?.click()}
                    >
                        Adjuntar imagen
                    </Button>
                </div>
            </div>

            {attachments.length > 0 && (
                <div className="mt-3 grid gap-3">
                    {attachments.map((attachment) => (
                        <div
                            key={attachment.id}
                            className="flex items-start gap-3 rounded-xl border border-default-200 bg-white p-3"
                        >
                            {attachment.previewDataUrl ? (
                                <img
                                    src={attachment.previewDataUrl}
                                    alt={`Adjunto ${attachment.name}`}
                                    className="h-16 w-16 rounded-lg object-cover"
                                />
                            ) : (
                                <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-default-100 text-default-500">
                                    <Icons.File className="h-6 w-6" />
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-default-800">
                                    {attachment.name}
                                </p>
                                <p className="text-xs text-default-500">
                                    {formatSupportAttachmentSize(
                                        attachment.size
                                    )}
                                </p>
                                {!attachment.previewDataUrl && (
                                    <p className="text-xs text-default-400">
                                        Vista previa no disponible, pero la
                                        referencia del archivo se enviara con el
                                        caso.
                                    </p>
                                )}
                            </div>
                            <Button
                                isIconOnly
                                size="sm"
                                color="danger"
                                variant="light"
                                onPress={() =>
                                    onRemoveAttachment(attachment.id)
                                }
                            >
                                <Icons.Times className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
